import React, { useState, useEffect, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { AuthContext } from '../Provider';
import { supabase } from '../data/supabaseClient';
import UserStats from './UserStats';
import ActivePosts from './ActivePosts';
import HistoryPosts from './HistoryPosts';
import { useToast } from '../hooks/ToastContext';
import Spinner from '../common/Spinner';
import './MyProfile.css';

const ActivityPage = () => {
  const { t } = useTranslation('common');
  const { user } = useContext(AuthContext);
  const { addToast } = useToast();

  const [activeTab, setActiveTab] = useState('active'); // 'active' or 'history'
  const [isLoading, setIsLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [cancellationStats, setCancellationStats] = useState(null);

  // Posts data
  const [activePosts, setActivePosts] = useState([]);
  const [activePackages, setActivePackages] = useState([]);
  const [archivedPosts, setArchivedPosts] = useState([]);
  const [archivedPackages, setArchivedPackages] = useState([]);

  // Fetch all activity data
  useEffect(() => {
    if (user?.id) {
      fetchActivityData();
    }
  }, [user?.id]);

  const fetchActivityData = async () => {
    try {
      setIsLoading(true);

      // Fetch user stats
      const { data: statsData, error: statsError } = await supabase
        .from('user_stats')
        .select('*')
        .eq('user_id', user.id)
        .single();

      if (statsError && statsError.code !== 'PGRST116') {
        console.error('Error fetching stats:', statsError);
      } else {
        setStats(statsData);
      }

      // Fetch cancellation stats
      const { data: cancellationData, error: cancellationError } = await supabase
        .rpc('get_user_cancellation_stats', { p_user_id: user.id });

      if (cancellationError) {
        console.error('Error fetching cancellation stats:', cancellationError);
      } else {
        setCancellationStats(cancellationData);
      }

      // Fetch active posts
      const { data: activePostsData, error: activePostsError } = await supabase
        .from('posts')
        .select('*')
        .eq('user_id', user.id)
        .eq('status', 'active')
        .order('created_at', { ascending: false });

      if (activePostsError) {
        console.error('Error fetching active posts:', activePostsError);
      } else {
        setActivePosts(activePostsData || []);
      }

      // Fetch active packages
      const { data: activePackagesData, error: activePackagesError } = await supabase
        .from('packages')
        .select('*')
        .eq('user_id', user.id)
        .eq('status', 'active')
        .order('created_at', { ascending: false });

      if (activePackagesError) {
        console.error('Error fetching active packages:', activePackagesError);
      } else {
        setActivePackages(activePackagesData || []);
      }

      // Fetch archived posts
      const { data: archivedPostsData, error: archivedPostsError } = await supabase
        .from('posts')
        .select('*')
        .eq('user_id', user.id)
        .in('status', ['archived', 'completed'])
        .order('created_at', { ascending: false });

      if (archivedPostsError) {
        console.error('Error fetching archived posts:', archivedPostsError);
      } else {
        setArchivedPosts(archivedPostsData || []);
      }

      // Fetch archived packages
      const { data: archivedPackagesData, error: archivedPackagesError } = await supabase
        .from('packages')
        .select('*')
        .eq('user_id', user.id)
        .in('status', ['archived', 'completed'])
        .order('created_at', { ascending: false });

      if (archivedPackagesError) {
        console.error('Error fetching archived packages:', archivedPackagesError);
      } else {
        setArchivedPackages(archivedPackagesData || []);
      }

    } catch (error) {
      console.error('Error in fetchActivityData:', error);
      addToast({
        type: 'error',
        title: t('errors.data_load_error'),
        description: t('profile.error_fetch_user')
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Handle post deletion (archive)
  const handleDeletePost = async (postId) => {
    try {
      const { error } = await supabase
        .from('posts')
        .update({ status: 'archived' })
        .eq('id', postId);

      if (error) throw error;

      // Optimistic update
      setActivePosts(activePosts.filter(post => post.id !== postId));
      addToast({
        type: 'success',
        title: t('posts.post_archived'),
        description: t('posts.post_archive_success')
      });
    } catch (error) {
      console.error('Error archiving post:', error);
      addToast({
        type: 'error',
        title: t('errors.archive_failed'),
        description: error.message
      });
    }
  };

  // Handle package deletion (archive)
  const handleDeletePackage = async (packageId) => {
    try {
      const { error } = await supabase
        .from('packages')
        .update({ status: 'archived' })
        .eq('id', packageId);

      if (error) throw error;

      // Optimistic update
      setActivePackages(activePackages.filter(pkg => pkg.id !== packageId));
      addToast({
        type: 'success',
        title: t('posts.package_archived'),
        description: t('posts.package_archive_success')
      });
    } catch (error) {
      console.error('Error archiving package:', error);
      addToast({
        type: 'error',
        title: t('errors.archive_failed'),
        description: error.message
      });
    }
  };

  if (isLoading) {
    return (
      <div className="activity-page">
        <div className="activity-container">
          <div className="centered-spinner">
            <Spinner size="large" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="activity-page">
      <div className="activity-container">
        <h1>{t('posts.my_activity')}</h1>

        {/* User Stats Section */}
        <UserStats
          stats={stats}
          activePosts={activePosts}
          activePackages={activePackages}
          archivedPosts={archivedPosts}
          archivedPackages={archivedPackages}
          cancellationStats={cancellationStats}
          isLoading={false}
        />

        {/* Tab Navigation */}
        <div className="activity-tabs">
          <button
            className={`tab-button ${activeTab === 'active' ? 'active' : ''}`}
            onClick={() => setActiveTab('active')}
          >
            {t('posts.active')}
          </button>
          <button
            className={`tab-button ${activeTab === 'history' ? 'active' : ''}`}
            onClick={() => setActiveTab('history')}
          >
            {t('posts.history')}
          </button>
        </div>

        {/* Tab Content */}
        <div className="tab-content">
          {activeTab === 'active' ? (
            <ActivePosts
              activePosts={activePosts}
              activePackages={activePackages}
              onDeletePost={handleDeletePost}
              onDeletePackage={handleDeletePackage}
              isLoading={false}
            />
          ) : (
            <HistoryPosts
              archivedPosts={archivedPosts}
              archivedPackages={archivedPackages}
              isLoading={false}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default ActivityPage;
